/*
 * Fovory search panel (snippets/fovory-search.liquid).
 * - Takes clicks on any header search trigger before Minimog's popup sees them.
 * - Desktop (>= 1024px): the card opens exactly over the header card.
 *   Smaller screens: full screen sheet (CSS).
 * - Idle: recent searches kept in this browser only (localStorage, best effort).
 * - Typing: Shopify predictive search rendered by sections/fovory-predictive-search.liquid.
 * - Esc, the scrim and the close button close it; focus stays inside while open
 *   and goes back to the icon that opened it.
 */
(() => {
  const root = document.querySelector('[data-fv-so]');
  if (!root || root.dataset.ready) return;
  root.dataset.ready = 'true';

  const panel = root.querySelector('.fv-so');
  const scrim = root.querySelector('[data-fv-so-scrim]');
  const form = root.querySelector('form');
  const input = root.querySelector('[data-fv-so-input]');
  const idle = root.querySelector('[data-fv-so-idle]');
  const results = root.querySelector('[data-fv-so-results]');
  const recentWrap = root.querySelector('[data-fv-so-recent]');
  const recentList = root.querySelector('[data-fv-so-recent-list]');
  const clearBtn = root.querySelector('[data-fv-so-clear]');
  const closeBtns = root.querySelectorAll('[data-fv-so-close]');
  const suggestUrl = root.dataset.url;
  const removeLabel = root.dataset.tRemove || 'Remove “[q]”';
  const KEY = 'fv:recent-searches';
  const TRIGGERS = '[data-open-search-popup], .m-header__search, .m-header__search-icon, [data-fv-search-open]';
  const X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7 7 17"/></svg>';

  const cache = new Map();
  let lastTrigger = null;
  let controller = null;
  let timer = null;

  const store = {
    get() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
    },
    set(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 5))); } catch (e) { /* private mode */ }
    },
  };

  const remember = (term) => {
    const t = (term || '').trim();
    if (!t) return;
    const list = store.get().filter((x) => x.toLowerCase() !== t.toLowerCase());
    list.unshift(t);
    store.set(list);
  };

  const searchHref = (t) => `${form.getAttribute('action')}?q=${encodeURIComponent(t)}&type=product&options%5Bprefix%5D=last`;

  function renderRecent() {
    const list = store.get();
    recentList.textContent = '';
    recentWrap.hidden = list.length === 0;
    list.forEach((t) => {
      const li = document.createElement('li');
      li.className = 'fv-so__row';
      const a = document.createElement('a');
      a.className = 'fv-so__r';
      a.href = searchHref(t);
      a.dataset.fvTerm = t;
      a.textContent = t;
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'fv-so__x';
      b.setAttribute('aria-label', removeLabel.replace('[q]', t));
      b.innerHTML = X;
      b.addEventListener('click', () => {
        store.set(store.get().filter((x) => x !== t));
        renderRecent();
        input.focus();
      });
      li.append(a, b);
      recentList.append(li);
    });
  }

  function placeOverHeader() {
    if (window.innerWidth < 1024) return;
    const card = document.querySelector('.m-header__desktop .m-header__container');
    const top = card ? Math.max(16, Math.round(card.getBoundingClientRect().top)) : 16;
    root.style.setProperty('--fv-so-top', `${top}px`);
  }

  function showIdle() {
    if (controller) controller.abort();
    results.hidden = true;
    results.textContent = '';
    idle.hidden = false;
  }

  // Bold the typed part of our own suggestions (Shopify's come with <mark> already).
  function highlight(q) {
    const needle = q.trim().toLowerCase();
    if (!needle) return;
    results.querySelectorAll('[data-fv-hl]').forEach((el) => {
      const text = el.textContent;
      const i = text.toLowerCase().indexOf(needle);
      if (i < 0) return;
      const mark = document.createElement('mark');
      mark.textContent = text.slice(i, i + needle.length);
      el.textContent = '';
      el.append(text.slice(0, i), mark, text.slice(i + needle.length));
    });
  }

  function showResults(html) {
    results.innerHTML = html;
    highlight(input.value);
    idle.hidden = true;
    results.hidden = false;
  }

  async function fetchResults(q) {
    const key = q.toLowerCase();
    if (cache.has(key)) {
      showResults(cache.get(key));
      return;
    }
    if (controller) controller.abort();
    controller = new AbortController();
    root.setAttribute('aria-busy', 'true');
    const params = new URLSearchParams({
      q,
      'resources[type]': 'query,product,collection',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,tag',
      section_id: 'fovory-predictive-search',
    });
    try {
      const res = await fetch(`${suggestUrl}?${params.toString()}`, { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-fovory-predictive-search');
      const html = section ? section.innerHTML : '';
      cache.set(key, html);
      if (input.value.trim().toLowerCase() === key) showResults(html);
    } catch (e) {
      if (e.name !== 'AbortError') showIdle();
    } finally {
      root.removeAttribute('aria-busy');
    }
  }

  function open(trigger) {
    lastTrigger = trigger || document.activeElement;
    placeOverHeader();
    renderRecent();
    root.hidden = false;
    document.documentElement.classList.add('fv-so-open');
    requestAnimationFrame(() => {
      root.classList.add('is-open');
      input.focus({ preventScroll: true });
      if (input.value) input.select();
    });
  }

  function close() {
    if (root.hidden) return;
    root.classList.remove('is-open');
    root.hidden = true;
    document.documentElement.classList.remove('fv-so-open');
    // Next time it opens clean, on recent searches and shortcuts.
    clearTimeout(timer);
    input.value = '';
    clearBtn.hidden = true;
    showIdle();
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      const focusable = lastTrigger.matches('button, a') ? lastTrigger : lastTrigger.querySelector('button, a');
      (focusable || lastTrigger).focus({ preventScroll: true });
    }
  }

  // Open from any header search icon, before Minimog's own popup handles the click.
  document.addEventListener(
    'click',
    (e) => {
      const t = e.target.closest(TRIGGERS);
      if (!t || root.contains(t)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open(t);
    },
    true
  );

  input.addEventListener('input', () => {
    const q = input.value.trim();
    clearBtn.hidden = input.value.length === 0;
    clearTimeout(timer);
    if (!q) {
      showIdle();
      return;
    }
    timer = setTimeout(() => fetchResults(q), 180);
  });

  form.addEventListener('submit', (e) => {
    const q = input.value.trim();
    if (!q) {
      e.preventDefault();
      return;
    }
    remember(q);
  });

  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-fv-term]');
    if (a) remember(a.dataset.fvTerm);
  });

  clearBtn.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    showIdle();
    input.focus();
  });

  closeBtns.forEach((b) => b.addEventListener('click', close));
  scrim.addEventListener('click', close);

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = [...panel.querySelectorAll('a[href], button, input:not([type="hidden"])')].filter(
      (el) => !el.hidden && el.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (!root.hidden) placeOverHeader();
  });
})();
